import { useEffect, useMemo, useState } from "react";
import { ConfirmationModal, PDFModal } from "../../../../../common/Modal";
import { base64ToFile, downloadFile, fileToBase64Old as fileToBase64 } from "../../../../../utils/fileUtils";

import GroupIcon from "@mui/icons-material/Group";
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { WorkflowStatus } from "../../../../../utils/enums";
import { Input, TextArea } from "../../../../../common/Input";
import { Button } from "../../../../../common/APICommon";
import FormPaginationControl from "../formComponents/FormPaginationControl";

export const CandidateForms = ({
  data,
  form, setForm,
  showGeneralMessage
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [candidatesPerPage] = useState(1);
  const [candidates, setCandidates] = useState([]);
  const [fileObjects, setFileObjects] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});

  const maxCandidates = parseInt(data?.numberOfVacancies) || 0;

  // Memoize the saved candidates for parent form update
  const savedCandidates = useMemo(() =>
    candidates
      .filter(candidate => candidate.isSaved)
      .map(({ isSaved, file, isExisting, ...candidate }) => ({
        ...candidate,
        file: candidate.fileBase64
      })),
    [candidates]
  );

  // Memoize the saved candidates count for display
  const savedCandidatesCount = useMemo(() =>
    candidates.filter(candidate => candidate.isSaved && candidate.isActive).length,
    [candidates]
  );

  // Initialize only once
  useEffect(() => {
    if (!isInitialized && maxCandidates > 0) {
      if (data?.candidates && data.candidates.length > 0) {
        const initializedCandidates = data.candidates.map((candidate) => {
          let fileUrl = null;
          if (candidate.file) {
            try {
              const file = base64ToFile(candidate.file, candidate.fileName, 'application/pdf');
              if (file) {
                fileUrl = URL.createObjectURL(file);
              }
            } catch (error) {
              console.error("Error creating file URL:", error);
            }
          }

          return {
            ...candidate,
            file: fileUrl,
            fileBase64: candidate.file,
            isSaved: true,
            isExisting: true
          };
        });
        setCandidates(initializedCandidates);
      } else {
        setCandidates([createEmptyCandidate()]);
      }
      setIsInitialized(true);
    }
  }, [maxCandidates, isInitialized, data?.candidates]);

  // Update parent form only when saved candidates actually change
  useEffect(() => {
    if (isInitialized) {
      setForm(prev => ({
        ...prev,
        candidates: savedCandidates
      }));
    }
  }, [savedCandidates, isInitialized, setForm]);

  const createEmptyCandidate = () => ({
    id: 0,
    firstName: "",
    lastName: "",
    middleName: "",
    extensionName: "",
    email: "",
    contactNumber: "",
    skills: "",
    experience: "",
    file: null,
    fileBase64: "",
    fileName: "",
    isAccepted: false,
    isDeclined: false,
    isActive: true,
    candidateQualificationNotes: "",
    processorComments: "",
    isSaved: false,
    isExisting: false
  });

  const handleAddCandidate = () => {
    if (candidates.length < maxCandidates) {
      setCandidates(prev => [...prev, createEmptyCandidate()]);
      setCurrentPage(Math.ceil((candidates.length + 1) / candidatesPerPage));
    }
  };

  const handleSaveCandidate = async (index) => {
    const candidate = candidates[index];

    // Validate required fields
    if (!candidate.firstName || !candidate.lastName || !candidate.email || !candidate.contactNumber || !candidate.skills) {
      showGeneralMessage("Please fill all required fields (First Name, Last Name, Email, Contact Number, Skills)", false);
      return;
    }

    setConfirmConfig({
      title: "Save Candidate",
      message: `Are you sure you want to save candidate #${index + 1}?`,
      confirmText: "Yes, Save",
      type: "info",
      onConfirm: () => confirmSaveCandidate(index)
    });
    setShowConfirmModal(true);
  };

  const confirmSaveCandidate = async (index) => {
    const candidate = candidates[index];

    const updatedCandidates = [...candidates];
    updatedCandidates[index] = {
      ...candidate,
      isSaved: true,
      id: candidate.id || 0
    };
    setCandidates(updatedCandidates);

    showGeneralMessage(`Candidate ${index + 1} saved successfully!`, true);

    if (candidates.length < maxCandidates && index === candidates.length - 1) {
      setTimeout(() => {
        handleAddCandidate();
      }, 500);
    }

    setShowConfirmModal(false);
  };

  const handleEditCandidate = (index) => {
    setConfirmConfig({
      title: "Edit Candidate",
      message: "Are you sure you want to edit this candidate? Unsaved changes will be lost.",
      confirmText: "Yes, Edit",
      type: "warning",
      onConfirm: () => confirmEditCandidate(index)
    });
    setShowConfirmModal(true);
  };

  const confirmEditCandidate = (index) => {
    const updatedCandidates = [...candidates];
    updatedCandidates[index] = { ...updatedCandidates[index], isSaved: false };
    setCandidates(updatedCandidates);
    setShowConfirmModal(false);
  };

  const handleDeleteCandidate = (index) => {
    const candidateToDelete = candidates[index];
    const constructedName = `${candidateToDelete.firstName || ''} ${candidateToDelete.lastName || ''}`.trim();
    const candidateName = constructedName || `Candidate #${index + 1}`;

    if (candidateToDelete.isExisting && candidateToDelete.id > 0) {
      setConfirmConfig({
        title: "Remove Candidate",
        message: `Are you sure you want to remove ${candidateName}? This will mark the candidate as inactive.`,
        confirmText: "Yes, Remove",
        type: "warning",
        onConfirm: () => confirmDeleteCandidate(index, true)
      });
    } else {
      setConfirmConfig({
        title: "Delete Candidate",
        message: `Are you sure you want to delete ${candidateName}? This action cannot be undone.`,
        confirmText: "Yes, Delete",
        type: "danger",
        onConfirm: () => confirmDeleteCandidate(index, false)
      });
    }
    setShowConfirmModal(true);
  };

  const confirmDeleteCandidate = (index, isExisting) => {
    const candidateToDelete = candidates[index];

    if (isExisting && candidateToDelete.isExisting && candidateToDelete.id > 0) {
      const updatedCandidates = [...candidates];
      updatedCandidates[index] = {
        ...candidateToDelete,
        isActive: false,
        isSaved: true
      };
      setCandidates(updatedCandidates);
    } else {
      const updatedCandidates = candidates.filter((_, i) => i !== index);
      setCandidates(updatedCandidates);

      const updatedFileObjects = { ...fileObjects };
      delete updatedFileObjects[index];
      setFileObjects(updatedFileObjects);
    }

    if (candidates.length > 0) {
      const newTotalPages = Math.ceil(candidates.length / candidatesPerPage);
      if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages);
      }
    } else {
      setCurrentPage(1);
      setCandidates([createEmptyCandidate()]);
    }

    setShowConfirmModal(false);
  };

  const updateCandidate = (index, field, value) => {
    const updatedCandidates = [...candidates];
    updatedCandidates[index] = { ...updatedCandidates[index], [field]: value };
    setCandidates(updatedCandidates);
  };

  const handleFileUpload = async (index, e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.type !== 'application/pdf') {
      showGeneralMessage("Please upload only PDF files for resumes.", false);
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showGeneralMessage("File size too large. Please upload PDF files smaller than 5MB.", false);
      e.target.value = '';
      return;
    }

    try {
      setConfirmConfig({
        title: "Upload Resume",
        message: `Upload ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB) for this candidate?`,
        confirmText: "Yes, Upload",
        type: "info",
        onConfirm: () => confirmFileUpload(index, file)
      });
      setShowConfirmModal(true);
    } catch (error) {
      console.error("Error preparing file upload:", error);
      showGeneralMessage("Error preparing file upload. Please try again.", false);
    }
  };

  const confirmFileUpload = async (index, file) => {
    try {
      const base64String = await fileToBase64(file);
      const fileUrl = URL.createObjectURL(file);

      setFileObjects(prev => ({
        ...prev,
        [index]: {
          file: file,
          url: fileUrl,
          name: file.name
        }
      }));

      const updatedCandidates = [...candidates];
      updatedCandidates[index] = {
        ...updatedCandidates[index],
        file: fileUrl,
        fileBase64: base64String,
        fileName: file.name
      };
      setCandidates(updatedCandidates);

      showGeneralMessage("PDF file uploaded successfully!", true);
    } catch (error) {
      console.error("Error uploading file:", error);
      showGeneralMessage("Error uploading file. Please try again.", false);
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleViewFile = (candidate) => {
    if (candidate.fileBase64) {
      try {
        const file = base64ToFile(candidate.fileBase64, candidate.fileName, 'application/pdf');
        if (file) {
          const fileUrl = URL.createObjectURL(file);
          setCurrentPdfUrl(fileUrl);
          setPdfModalVisible(true);
        }
      } catch (error) {
        console.error("Error converting base64 to file:", error);
        showGeneralMessage("Error opening PDF file.", false);
      }
    } else {
      showGeneralMessage("No PDF file available to view.", false);
    }
  };

  const handleDownloadFile = (candidate) => {
    if (candidate.fileBase64) {
      setConfirmConfig({
        title: "Download Resume",
        message: `Download ${candidate.fileName || 'resume.pdf'}?`,
        confirmText: "Yes, Download",
        type: "info",
        onConfirm: () => {
          downloadFile({
            file: candidate.fileBase64,
            fileName: candidate.fileName || 'resume.pdf',
            mimeType: 'application/pdf'
          });
          setShowConfirmModal(false);
        }
      });
      setShowConfirmModal(true);
    } else {
      showGeneralMessage("No PDF file available to download.", false);
    }
  };

  const closePdfModal = () => {
    setPdfModalVisible(false);
    if (currentPdfUrl && currentPdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentPdfUrl);
    }
    setCurrentPdfUrl(null);
  };

  const totalPages = Math.ceil(candidates.length / candidatesPerPage);
  const indexOfLastCandidate = currentPage * candidatesPerPage;
  const indexOfFirstCandidate = indexOfLastCandidate - candidatesPerPage;
  const currentCandidates = candidates.slice(indexOfFirstCandidate, indexOfLastCandidate);
  const canAddMore = candidates.filter(candidate => candidate.isActive).length < maxCandidates;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Progress Indicator */}
        {data.status === WorkflowStatus.PROCESSING && (
          <div className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">Candidate Progress</h3>
              <span className="text-sm text-gray-500">
                {savedCandidatesCount} of {maxCandidates} candidates saved
              </span>
            </div>

            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-green-500 transition-all duration-300"
                style={{ width: `${(savedCandidatesCount / maxCandidates) * 100}%` }}
              ></div>
            </div>
            {savedCandidatesCount === maxCandidates && (
              <p className="mt-2 text-center text-sm text-green-600">
                ✓ All candidates have been saved!
              </p>
            )}
          </div>
        )}

        {/* Current Candidate Form or Empty State */}
        {candidates.filter(c => c.isActive).length === 0 ? (
          <div className="rounded-lg border border-gray-300 bg-white p-8 text-center shadow-sm">
            <GroupIcon className="mx-auto mb-3 text-4xl text-gray-400" />
            <p className="mb-4 text-gray-500">No candidate forms available.</p>
            <button
              onClick={handleAddCandidate}
              className="rounded-lg border-none bg-blue-500 px-6 py-2 text-white transition-colors hover:bg-blue-600"
            >
              + Add First Candidate
            </button>
          </div>
        ) : (
          <>
            {/* Current Candidate Form */}
            {currentCandidates
              .filter(candidate => candidate.isActive)
              .map((candidate, index) => {
                const globalIndex = indexOfFirstCandidate + index;
                const isSaved = candidate.isSaved;

                return (
                  <div key={`candidate-${candidate.id || 'new'}-${globalIndex}`} className="rounded-lg border border-gray-300 bg-white p-6 shadow-sm">
                    <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-3">
                      <div className="flex items-center space-x-4">
                        <h4 className="text-lg font-semibold text-gray-500">
                          Candidate #{globalIndex + 1} {isSaved && "✓"}
                        </h4>
                        {isSaved && (
                          <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                            Saved
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          Page {currentPage} of {totalPages}
                        </span>
                        {data?.status === WorkflowStatus.PROCESSING && (
                          <>
                            {isSaved ? (
                              <button
                                onClick={() => handleEditCandidate(globalIndex)}
                                className="rounded border-none bg-yellow-500 px-3 py-1 text-sm text-white transition-colors hover:bg-yellow-600"
                              >
                                Edit
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDeleteCandidate(globalIndex)}
                                className="rounded border-none bg-red-500 px-3 py-1 text-sm text-white transition-colors hover:bg-red-600"
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {/* Name Inputs */}
                      <Input
                        label="First Name"
                        value={candidate.firstName || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'firstName', e.target.value)}
                        placeholder="Enter first name"
                        isRequired={true}
                        disabled={isSaved}
                      />
                      <Input
                        label="Last Name"
                        value={candidate.lastName || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'lastName', e.target.value)}
                        placeholder="Enter last name"
                        isRequired={true}
                        disabled={isSaved}
                      />
                      <Input
                        label="Middle Name"
                        value={candidate.middleName || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'middleName', e.target.value)}
                        placeholder="Enter middle name"
                        isRequired={false}
                        disabled={isSaved}
                      />
                      <Input
                        label="Extension Name"
                        value={candidate.extensionName || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'extensionName', e.target.value)}
                        placeholder="e.g. Jr., Sr., III"
                        isRequired={false}
                        disabled={isSaved}
                      />

                      {/* Restored Missing Inputs */}
                      <Input
                        label="Email"
                        type="email"
                        value={candidate.email || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'email', e.target.value)}
                        placeholder="Enter email address"
                        isRequired={true}
                        disabled={isSaved}
                      />
                      <Input
                        label="Contact Number"
                        value={candidate.contactNumber || ""}
                        type={"number"}
                        onChange={(e) => updateCandidate(globalIndex, 'contactNumber', e.target.value)}
                        placeholder="Enter contact number"
                        isRequired={true}
                        disabled={isSaved}
                      />
                      <Input
                        label="Skills"
                        value={candidate.skills || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'skills', e.target.value)}
                        placeholder="Enter key skills"
                        isRequired={true}
                        disabled={isSaved}
                      />
                      <Input
                        label="Experience"
                        value={candidate.experience || ""}
                        onChange={(e) => updateCandidate(globalIndex, 'experience', e.target.value)}
                        placeholder="Enter experience"
                        disabled={isSaved}
                      />

                      {/* File Upload Section */}
                      <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-3 md:col-span-2 lg:col-span-2">
                        <div className="col-span-2 flex h-full flex-col">
                          <label className="mb-1.5 block text-sm font-medium text-gray-500">
                            Resume / CV (PDF only)
                          </label>
                          <div className="flex flex-1 items-center rounded-lg border border-red-300 bg-gray-100">
                            <input
                              type="file"
                              onChange={(e) => handleFileUpload(globalIndex, e)}
                              disabled={isSaved}
                              accept=".pdf,application/pdf"
                              className="box-border block h-full w-full flex-1 cursor-pointer text-sm text-gray-500 file:mr-4 file:h-full file:rounded-lg file:border-0 file:bg-green-500 file:px-4 file:py-1 file:text-sm file:font-semibold file:text-white hover:file:bg-green-600 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                          {candidate.fileName && (
                            <p className="mt-1 truncate text-xs text-gray-500">
                              Selected: {candidate.fileName}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Only PDF files are accepted. Max size: 5MB
                          </p>
                        </div>

                        <div className="col-span-1 flex h-full flex-wrap items-center justify-start gap-2 md:justify-center">
                          <Button
                            icon={<VisibilityIcon />}
                            name="View"
                            className="flex h-[35px] w-full max-w-[80px] cursor-pointer items-center justify-center bg-blue-500 hover:bg-blue-600 md:min-w-[85px] md:flex-1 lg:w-auto"
                            onClick={() => handleViewFile(candidate)}
                            disabled={!candidate.file && !candidate.fileBase64}
                          />
                          <Button
                            icon={<DownloadIcon />}
                            name="Download"
                            className="flex h-[35px] w-full min-w-[102px] max-w-[80px] cursor-pointer items-center justify-center bg-green-500 hover:bg-green-600 md:flex-1 lg:w-auto"
                            onClick={() => handleDownloadFile(candidate)}
                            disabled={!candidate.fileBase64}
                          />
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <TextArea
                          label="Candidate Qualification Notes"
                          value={candidate.candidateQualificationNotes || ""}
                          onChange={(e) => updateCandidate(globalIndex, 'candidateQualificationNotes', e.target.value)}
                          placeholder="Document the candidate's qualifications, skills observed during interview, strengths, weaknesses, and overall assessment..."
                          rows={4}
                          disabled={isSaved}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <TextArea
                          label="Processor Comments"
                          value={candidate.processorComments || ""}
                          onChange={(e) => updateCandidate(globalIndex, 'processorComments', e.target.value)}
                          placeholder="Any additional comments, recommendations, or hiring considerations..."
                          rows={3}
                          disabled={isSaved}
                        />
                      </div>
                    </div>

                    {/* Save Button for unsaved forms */}
                    {!isSaved && (
                      <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                        <button
                          onClick={() => handleSaveCandidate(globalIndex)}
                          className="rounded-md border-none bg-green-500 px-6 py-2 text-white transition-colors hover:bg-green-600"
                        >
                          Save Candidate
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Add More Button */}
            {(canAddMore && data.status === WorkflowStatus.PROCESSING) && (
              <div className="flex justify-center">
                <button
                  onClick={handleAddCandidate}
                  className="flex items-center space-x-2 rounded-lg border-none bg-blue-500 px-6 py-3 text-white transition-colors hover:bg-blue-600"
                >
                  <span>+ Add Another Candidate</span>
                </button>
              </div>
            )}

            <FormPaginationControl
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={candidates.filter(c => c.isActive).length}
              itemsPerPage={candidatesPerPage}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* PDF Modal */}
      <PDFModal
        fileUrl={currentPdfUrl}
        visible={pdfModalVisible}
        onClose={closePdfModal}
      />

      {/* Confirmation Modal for Save/Edit/Delete */}
      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        type={confirmConfig.type}
      />
    </>
  );
};